import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';

/** Выполняет один запрос формы; текст серверного отказа сохраняется для пользователя. */
export async function taskRequest(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  const data = await response.json();
  if (!response.ok) {
    const messages = {
      no_matching_grant: 'Нет разрешения на это действие в выбранной области.',
      read_only: 'В режиме чтения изменения запрещены.',
      ambiguous_holder: 'На посту несколько равноправных исполнителей. Уточните назначения.',
      no_eligible_holder: 'На посту нет подходящего исполнителя.',
      assignee_mismatch: 'Исполнитель поста изменился. Обновите карточку.',
      task_dependencies_incomplete: 'Сначала завершите предшествующие задачи.',
    };
    throw new Error(messages[data.code] || data.message || `Ошибка ${response.status}`);
  }
  return data;
}

// datetime-local показывает время пользователя, а API хранит ISO в UTC.
function localDate(value) {
  if (!value) return '';
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

/**
 * Форма v1 для tasks=enforce. Частичные команды разделены по правам:
 * сохранение текста не передаёт assignee, завершение не повторяет весь документ.
 * Метаданные помогают интерфейсу; backend повторно проверяет каждую команду.
 */
export default function EnforcedTaskForm({ id, initial, reload }) {
  const navigate = useNavigate();
  const isNew = id === 'new';
  const [form, setForm] = useState(initial.item);
  const [access, setAccess] = useState(initial);
  const [positions, setPositions] = useState(initial.routing?.positions || []);
  const [targetPosition, setTargetPosition] = useState('');
  const [projectQuery, setProjectQuery] = useState('');
  const [projects, setProjects] = useState([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const permissions = access.actions || {};
  const editable = isNew || permissions.update;
  const endpoint = `/api/directories/tasks/${id}`;
  const watcherOptions = new Map(
    positions.map((position) => [position.assignee._id, position.assignee]),
  );

  // Сохранённый ID не разрешает читать имя пользователя. Оставляем неизвестных
  // наблюдателей выбранными, не превращая карточку задачи в общий users lookup.
  for (const watcher of initial.item.watchers || []) {
    const watcherId = watcher?._id || watcher;
    if (!watcherOptions.has(watcherId)) {
      watcherOptions.set(watcherId, {
        _id: watcherId,
        name: 'Наблюдатель — имя недоступно',
      });
    }
  }

  const watcherIds = (form.watchers || []).map((watcher) => watcher._id || watcher);
  const selected = positions.find((position) => position._id === targetPosition);
  const setField = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));

  async function perform(operation) {
    setBusy(true);
    setMessage('');
    try {
      await operation();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function searchProjects() {
    await perform(async () => {
      const result = await taskRequest(`/api/lookup/projects?q=${encodeURIComponent(projectQuery)}`);
      setProjects(result.items);
      if (!result.items.length) {
        setMessage('По этому запросу нет доступных проектов. Уточните название или права подбора.');
      }
    });
  }

  async function selectProject(project) {
    setField('project', project);
    setTargetPosition('');
    setPositions([]);
    setAccess((previous) => ({ ...previous, actions: { create: false } }));
    if (!project) return;
    await perform(async () => {
      const result = await taskRequest(`${endpoint}?project=${encodeURIComponent(project)}`);
      setAccess(result);
      setPositions(result.routing.positions);
    });
  }

  async function save(body, method = 'PUT') {
    await perform(async () => {
      const result = await taskRequest(endpoint, { method, body: JSON.stringify(body) });
      if (isNew) {
        navigate(`/tasks/${result._id}`);
      } else {
        await reload();
      }
    });
  }

  function saveDetails(event) {
    event.preventDefault();
    const body = {
      title: form.title,
      description: form.description,
      isImportant: Boolean(form.isImportant),
      isUrgent: Boolean(form.isUrgent),
      canComplete: form.canComplete || 'both',
      watchers: watcherIds,
      dates: { plannedEnd: form.dates?.plannedEnd || null },
    };
    if (isNew) {
      Object.assign(body, { project: form.project, targetPosition,
        assignee: selected?.assignee._id });
    }
    save(body, isNew ? 'POST' : 'PUT');
  }

  return (
    <Box sx={{ maxWidth: 850, mx: 'auto', p: 3 }}>
      <Stack spacing={2}>
        <Link to='/tasks'>Все задачи</Link>
        <Typography variant='h4'>{isNew ? 'Новая задача' : form.title}</Typography>
        {message && <Alert severity='error'>{message}</Alert>}
        {!isNew && <Typography>Статус: {form.status}. {form.visible ? 'Видна' : 'Скрыта'}</Typography>}
        {isNew && (
          <Stack spacing={2}>
            <TextField label='Поиск проекта' value={projectQuery}
              onChange={(event) => setProjectQuery(event.target.value)} />
            <Button onClick={searchProjects} disabled={busy}>Найти проекты</Button>
            <TextField select label='Проект' value={form.project || ''}
              disabled={busy} onChange={(event) => selectProject(event.target.value)}>
              <MenuItem value=''>Выберите проект</MenuItem>
              {projects.map((project) => <MenuItem key={project.id} value={project.id}>{project.label}</MenuItem>)}
            </TextField>
          </Stack>
        )}
        <Box component='form' onSubmit={saveDetails}>
          <Stack spacing={2}>
            <TextField label='Название' required value={form.title || ''} disabled={!editable || busy}
              onChange={(event) => setField('title', event.target.value)} />
            <TextField label='Описание' multiline minRows={4} value={form.description || ''}
              disabled={!editable || busy} onChange={(event) => setField('description', event.target.value)} />
            <TextField label='Планирую завершить' type='datetime-local' InputLabelProps={{ shrink: true }}
              value={localDate(form.dates?.plannedEnd)}
              disabled={!editable || busy} onChange={(event) => setField('dates', {
                ...form.dates, plannedEnd: event.target.value ? new Date(event.target.value).toISOString() : null,
              })} />
            <FormControlLabel label='Важная' control={<Checkbox checked={Boolean(form.isImportant)}
              disabled={!editable || busy} onChange={(event) => setField('isImportant', event.target.checked)} />} />
            <FormControlLabel label='Срочная' control={<Checkbox checked={Boolean(form.isUrgent)}
              disabled={!editable || busy} onChange={(event) => setField('isUrgent', event.target.checked)} />} />
            <TextField select label='Наблюдатели' SelectProps={{ multiple: true }}
              value={watcherIds} disabled={!editable || busy}
              helperText='Имена показаны для доступных адресатов. Остальные наблюдатели сохранены.'
              onChange={(event) => setField('watchers', event.target.value)}>
              {[...watcherOptions.values()].map((watcher) => <MenuItem key={watcher._id} value={watcher._id}>
                {watcher.name}
              </MenuItem>)}
            </TextField>
            <TextField select label='Кто завершает' value={form.canComplete || 'both'} disabled={!editable || busy}
              helperText='Для завершения также требуется отдельное разрешение.'
              onChange={(event) => setField('canComplete', event.target.value)}>
              <MenuItem value='assignee'>Исполнитель</MenuItem>
              <MenuItem value='creator'>Постановщик</MenuItem>
              <MenuItem value='both'>Оба</MenuItem>
            </TextField>
            {editable && <Button type='submit' variant='contained'
              disabled={busy || (isNew && (!permissions.create || !selected))}>
              {isNew ? 'Создать задачу' : 'Сохранить изменения'}
            </Button>}
          </Stack>
        </Box>
        {!isNew && <Typography>Исполнитель: {form.assignee?.name || 'не указан'}</Typography>}
        {(isNew || permissions.delegate) && !form.isTemplate && (
          <Stack spacing={2}>
            <TextField select label='Пост исполнителя' value={targetPosition} disabled={busy}
              onChange={(event) => setTargetPosition(event.target.value)}>
              <MenuItem value=''>Выберите пост</MenuItem>
              {positions.map((position) => <MenuItem key={position._id} value={position._id}>
                {position.title} — {position.assignee.name}
              </MenuItem>)}
            </TextField>
            {selected && <Typography>Выбран исполнитель: {selected.assignee.name}</Typography>}
            {!positions.length && <Typography>Нет доступных адресатов в выбранной области.</Typography>}
            {!isNew && <Button disabled={busy || !selected}
              onClick={() => save({ targetPosition, assignee: selected.assignee._id })}>Делегировать</Button>}
          </Stack>
        )}
        {!isNew && !form.isTemplate && !['completed', 'cancelled'].includes(form.status) && (
          <Stack direction='row' spacing={2}>
            {permissions.update && <Button disabled={busy} onClick={() => save({ status: 'in_progress' })}>Начать работу</Button>}
            {permissions.complete && <Button disabled={busy} onClick={() => save({ status: 'completed' })}>Завершить задачу</Button>}
            {permissions.complete && <Button disabled={busy} onClick={() => save({ status: 'cancelled' })}>Отменить задачу</Button>}
          </Stack>
        )}
        {!isNew && permissions.delete && <Button disabled={busy} onClick={() => save(undefined, 'DELETE')}>
          {form.visible ? 'Скрыть задачу' : 'Восстановить задачу'}
        </Button>}
      </Stack>
    </Box>
  );
}
